import APP_ASSETS from "@/assets";
import { OrderDrawer } from "@/components/OrderDrawer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StateStatusEnum } from "@/enums/state-status.enum";
import { formatTimestamp } from "@/lib/utils";
import { IOrderItem, OrderStatus, OrderType } from "@/models";
import { isOrderExpired } from "@/utils/orders";
import { ethers } from "ethers";
import { Wallet2Icon } from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { trackAmplitudeEvent } from "@/utils/amplitude";

export const NewOrderItem = ({
  id,
  orderType,
  fiatAmount,
  placedTimestamp,
  status,
}: IOrderItem) => {
  return (
    <div className="flex w-full items-center justify-between gap-2 rounded-lg bg-bg-grey px-4 py-2">
      <div className="flex items-center justify-between gap-2">
        <Wallet2Icon className="h-9 w-9 rounded-full border border-slate-700 object-contain p-2 text-slate-400" />
        <div className="flex flex-col items-start">
          <div className="flex items-center">
            <p className="text-sm font-medium tracking-wide text-text-light">
              {orderType === OrderType.SELL ? "Send" : "Receive"} INR
            </p>
            <div className="mx-2 flex items-center justify-center rounded-sm bg-yellow-500 px-1">
              <p className="text-[10px] font-black tracking-widest text-black">
                {OrderStatus[status]}
              </p>
            </div>
          </div>
          <p className="text-sm tracking-wide text-gray-400">Order ID: {id}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex flex-col items-end justify-between">
          <div className="flex gap-1">
            <p className="font-medium text-text-light">
              {Math.round(Number(ethers.utils.formatUnits(fiatAmount, 6)))
                .toFixed(2)
                .toString()}
            </p>
            <img src={APP_ASSETS.TOKENS.INR_TRANSPARENT} alt="INR" />
          </div>
          <p className="text-[12px] tracking-wide text-gray-400">
            {formatTimestamp(placedTimestamp)}
          </p>
        </div>
      </div>
    </div>
  );
};

const NewOrders = () => {
  const [openedOrderId, setOpenedOrderId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "all" | OrderType.BUY | OrderType.SELL
  >("all");

  const conditionChecks = (order: IOrderItem) => {
    return (
      (activeTab === "all" || order.orderType == activeTab) &&
      isOrderExpired(parseInt(order.placedTimestamp))
    ); // filter by transaction types
  };

  const ordersData = useSelector((state: any) => state.orders.data);
  const isLoading = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.LOADING,
  );
  const hasFetched = useSelector(
    (state: any) => state.orders.status == StateStatusEnum.SUCCESS,
  );

  return (
    <main className="relative h-full max-h-[1200px] w-full max-w-[500px] bg-bg-tertiary">
      {/* ABSOLUTE POSITIONED TOPBAR */}
      <nav className="absolute top-0 flex h-14 w-full items-center justify-start gap-2 border-b border-primary bg-bg-tertiary p-4">
        <Link to={".."} relative="path">
          <img
            className="h-6 w-6"
            src={APP_ASSETS.ICONS.ARROW_LEFT}
            alt="img"
          />
        </Link>
        <div className="flex w-full items-center justify-between">
          <p className="text-xl text-text-light">New Orders</p>
        </div>
      </nav>
      {/* ABSOLUTE POSITIONED TOPBAR */}
      {/* NEW ORDERS */}
      <section className="no-scrollbar flex h-full flex-col items-start overflow-y-scroll pt-14">
        {/* FILTERS */}
        <div className="flex w-full items-center justify-start gap-6 px-6 py-4">
          <Button
            onClick={() => {
              setActiveTab("all");
            }}
            variant={activeTab === "all" ? "default" : "outline"}
            className="h-8 rounded-full">
            All
          </Button>
          <Button
            onClick={() => {
              setActiveTab(OrderType.SELL);
            }}
            variant={activeTab === OrderType.SELL ? "default" : "outline"}
            className="h-8 rounded-full">
            Send
          </Button>
          <Button
            onClick={() => {
              setActiveTab(OrderType.BUY);
            }}
            variant={activeTab === OrderType.BUY ? "default" : "outline"}
            className="h-8 rounded-full">
            Receive
          </Button>
        </div>
        {/* FILTERS */}
        {/* VOLUME LIST */}
        <div className="mx-4 flex w-[93%] flex-col gap-4">
          {isLoading || !hasFetched ? (
            <div className="flex flex-col items-center justify-center gap-4">
              {Array(12)
                .fill(1)
                .map((_, idx) => (
                  <Skeleton key={idx} className="h-16 w-[90%]" />
                ))}
            </div>
          ) : (
            ordersData?.latestOrders
              ?.toJS()
              .filter(conditionChecks)
              .map((order: any, idx: any) => (
                <div
                  className="hover:cursor-pointer"
                  onClick={() => {
                    // @ts-ignore
                    setOpenedOrderId(order.id);
                    setDrawerOpen(true);
                    trackAmplitudeEvent("VIEW_ORDER_DETAILS_NEW_ORDERS");
                  }}>
                  <NewOrderItem key={idx} {...order} />
                </div>
              ))
          )}
        </div>
        {/* LIST */}
      </section>
      {openedOrderId && (
        <OrderDrawer
          order={ordersData?.allOrders
            ?.toJS()
            ?.find((_: any) => _.id == openedOrderId)}
          autoOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          isLoading={isLoading}
        />
      )}
      {/* NEW ORDERS */}
    </main>
  );
};

export default NewOrders;
